import { z } from 'zod';
/**
 * Streamer Dashboard REST DTO — Phase 6 / Plan 06-02 (DASH-01).
 *
 * 엔드포인트:
 *   - `GET /api/v1/streamers/me/kpis`
 *       — 오늘/이번주/이번달 sum + 24h 델타 + 활성 goal 진행률
 *   - `GET /api/v1/streamers/me/donations?limit&cursor&status&fanQuery&from&to`
 *       — 최근 후원 cursor-pagination (2026-04-22 Wave 0: 기본 10-20건)
 *
 * 불변 (06-02-PLAN behavior):
 *   - 금액은 항상 `number` (KRW). bigint 는 서버 내부 계산 전용.
 *   - deltaPct: `((today - previousDay) / previousDay) * 100` · 이전 0 이면 null.
 *   - goal.pct: 0..100 clamp 된 진행률.
 *   - cursor: base64-encoded `{ createdAt: ISO, id: UUID }` (controller 가 decode).
 *   - refundable: `status === 'CONFIRMED' && approvedAt < 30d`.
 */
// ---------------------------------------------------------------------------
// GET /api/v1/streamers/me/kpis
// ---------------------------------------------------------------------------
const kpiAmountSchema = z.object({
    /** 기간 내 합계 (KRW). status=CONFIRMED 의 amount_net 합 - 해당 payment 에 대한 환불 합. */
    amountKrw: z.number().int().nonnegative(),
    /**
     * 24시간 델타 (백분율, 소수 1자리). 이전 기간 합계가 0 이면 null.
     * 양수 → 증가, 음수 → 감소. UI 가 ▲/▼ 아이콘 분기.
     */
    deltaPct: z.number().nullable(),
});
const kpiGoalSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1),
    currentKrw: z.number().int().nonnegative(),
    targetKrw: z.number().int().positive(),
    /** 진행률 — `Math.min(100, Math.round(current/target * 100))`. */
    pct: z.number().min(0).max(100),
});
export const streamerKpisResponseSchema = z.object({
    today: kpiAmountSchema,
    week: kpiAmountSchema,
    month: kpiAmountSchema,
    goal: kpiGoalSchema.nullable(),
    /** ISO 8601 with offset — 서버가 계산을 확정한 시각 (캐시 hit 여부 판별 참고). */
    computedAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// GET /api/v1/streamers/me/donations
// ---------------------------------------------------------------------------
/** 결제 상태 enum (packages/db/src/schema/payment.ts paymentStatusEnum 일부). */
export const streamerDonationStatusSchema = z.enum([
    'CONFIRMED',
    'REFUNDED',
    'FAILED',
    'PARTIAL_CANCELED',
    'CANCELED',
]);
export const streamerDonationMethodSchema = z.enum([
    'CARD',
    'KAKAOPAY',
    'NAVERPAY',
    'TOSSPAY',
]);
export const donationRowSchema = z.object({
    id: z.string().uuid(),
    /** Payment 승인 시각 (CONFIRMED) 또는 createdAt (그 외) — ISO 8601 with offset. */
    receivedAt: z.string().datetime({ offset: true }),
    nickname: z.string(),
    /** Fan UUID — PII 포함 금지 (email·phone 금지). Wave 0 은 fanId 자체도 UUID 만 공개. */
    fanId: z.string().uuid().nullable(),
    amountKrw: z.number().int().nonnegative(),
    method: streamerDonationMethodSchema,
    status: streamerDonationStatusSchema,
    message: z.string().nullable(),
    /**
     * 환불 가능 여부 — `status === 'CONFIRMED' && approvedAt 가 30일 이내`.
     * UI 가 hover 환불 버튼 활성 결정에 사용 (실 환불은 Plan 06-03 Wave).
     */
    refundable: z.boolean(),
});
export const recentDonationsResponseSchema = z.object({
    rows: z.array(donationRowSchema),
    /**
     * 다음 페이지 cursor. base64-encoded `{ createdAt: ISO, id: UUID }`.
     * `null` 이면 더 이상 페이지 없음.
     */
    nextCursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// POST /api/v1/moderation/me/blocked-fans — Phase 6 / Plan 06-03 Task 1 (DASH-02)
// ---------------------------------------------------------------------------
export const blockedFanRowSchema = z.object({
    id: z.string().uuid(),
    streamerId: z.string().uuid(),
    fanId: z.string().uuid(),
    reason: z.string().nullable(),
    /** ISO 8601 with offset — 차단이 확정된 시각. */
    blockedAt: z.string().datetime({ offset: true }),
});
export const blockedFansListResponseSchema = z.object({
    items: z.array(blockedFanRowSchema),
});
export const addBlockedFanRequestSchema = z.object({
    fanId: z.string().uuid(),
    /** 스트리머가 남길 선택적 사유 — max 200자. */
    reason: z.string().max(200).nullable().optional(),
});
// ---------------------------------------------------------------------------
// GET /api/v1/streamers/me/settlements — Phase 6 / Plan 06-05 Task 1 (DASH polish)
// ---------------------------------------------------------------------------
export const settlementStatusSchema = z.enum([
    'READY',
    'IN_PROGRESS',
    'DONE',
    'FAILED',
]);
export const settlementRowSchema = z.object({
    id: z.string().uuid(),
    /** 정산 예정 · 완료 시각 (completedAt 우선, 없으면 scheduledAt) — ISO 8601 with offset. */
    settledAt: z.string().datetime({ offset: true }),
    /** 총 후원 원가 합 (KRW). 본 payout 에 포함된 payments 의 amount_gross 합. */
    grossKrw: z.number().int().nonnegative(),
    /** 수수료 합 (fan + streamer). */
    feeKrw: z.number().int().nonnegative(),
    /** 실지급 (amount_net). */
    netKrw: z.number().int().nonnegative(),
    status: settlementStatusSchema,
});
export const settlementListResponseSchema = z.object({
    /** 정산 예정 합계 (READY + IN_PROGRESS status 의 amount_krw 합). */
    upcomingKrw: z.number().int().nonnegative(),
    /** 이번 달 수수료 합 (KST 기준 월 시작 이후 완료된 payout 기반 추정). */
    feesThisMonthKrw: z.number().int().nonnegative(),
    /** 다음 정산 예정일 — 최소 scheduled_at (READY+IN_PROGRESS). 없으면 null. */
    nextPayoutDate: z.string().datetime({ offset: true }).nullable(),
    rows: z.array(settlementRowSchema),
});
// ---------------------------------------------------------------------------
// GET /api/v1/widgets/me/goals/history — Phase 6 / Plan 06-05 Task 1 (DASH polish)
// ---------------------------------------------------------------------------
export const goalHistoryRowSchema = z.object({
    id: z.string().uuid(),
    streamerId: z.string().uuid(),
    title: z.string().min(1).max(200),
    targetKrw: z.number().int().nonnegative(),
    currentKrw: z.number().int().nonnegative(),
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }).nullable(),
    reachedAt: z.string().datetime({ offset: true }).nullable(),
    active: z.literal(false),
});
export const goalHistoryResponseSchema = z.object({
    items: z.array(goalHistoryRowSchema),
});
// ---------------------------------------------------------------------------
// GET /api/v1/streamers/me/profile — Phase 6 / Plan 06-05 Task 1 (settings page)
// ---------------------------------------------------------------------------
export const streamerProfileResponseSchema = z.object({
    userId: z.string().uuid(),
    streamerId: z.string().uuid(),
    email: z.string().email().nullable(),
    handle: z.string(),
    displayName: z.string(),
    bio: z.string().nullable(),
    tier: z.string(),
    /** Toss 셀러 심사 상태 — 'NOT_SUBMITTED' | 'PARTIALLY_APPROVED' | 'FULLY_APPROVED' 등. */
    tossSellerStatus: z.string(),
});
export const activeSessionRowSchema = z.object({
    /** 세션 토큰 hash prefix (PII 보호) — SHA-256 앞 8자만 공개. */
    sessionIdPrefix: z.string().length(8),
    /** 해당 요청이 이 세션으로 들어왔는지 여부 — "이 기기" 뱃지용. */
    current: z.boolean(),
    /** 만료 시각 (Auth.js rolling 30d). */
    expiresAt: z.string().datetime({ offset: true }),
});
export const activeSessionsResponseSchema = z.object({
    items: z.array(activeSessionRowSchema),
});
//# sourceMappingURL=streamer-dashboard.rest.js.map